import { httpsCallable } from "firebase/functions";
import {
  CALLABLE_FUNCTIONS,
  DATA_CHANNEL,
  FILE_TRANSFER_LIMITS,
  type ActiveEncodeMode,
  type ControlMessage,
  type EncodingPreference,
  type EphemeralTurnCredentials,
  type FileTransferChunkMeta,
  type FileTransferDone,
  type FileTransferHello,
  type InputKeyboardMessage,
  type InputMouseMessage,
  type InputWheelMessage,
  type MintTurnCredentialsRequest,
  type MintTurnCredentialsResponse,
  type ModeSwitchMessage,
  type QualityMode,
  type QualityUpdateMessage,
  type SessionAuthMessage,
  type SessionSignalDocument,
  type StatsReportMessage,
} from "@srd/shared";
import { functions } from "../firebase";
import { TileCompositor, type CompositorStats, type CursorState } from "./tileCompositor";
import { postSignal, subscribeSessionSignaling } from "./signaling";

export type ViewerConnectionState =
  | "idle"
  | "connecting"
  | "connected"
  | "failed"
  | "closed";

export interface ViewerSessionOptions {
  sessionId: string;
  sessionJwt: string;
  turn: EphemeralTurnCredentials;
  canvas: HTMLCanvasElement;
  video: HTMLVideoElement;
  preferredMode: QualityMode;
  encodingPreference: EncodingPreference;
  onState?: (state: ViewerConnectionState) => void;
  onStats?: (stats: CompositorStats) => void;
  onCursor?: (cursor: CursorState) => void;
  onEncodeMode?: (mode: ActiveEncodeMode) => void;
  onRemoteTrack?: (hasVideo: boolean) => void;
  onError?: (message: string) => void;
  onFileEvent?: (event: {
    type: "hello" | "chunk" | "done" | "error";
    transferId: string;
    name?: string;
    size?: number;
    reason?: string;
    progress?: number;
  }) => void;
}

function iceServersFromTurn(turn: EphemeralTurnCredentials): RTCIceServer[] {
  return [
    { urls: "stun:stun.l.google.com:19302" },
    {
      urls: turn.uris,
      username: turn.username,
      credential: turn.credential,
    },
  ];
}

function parseControl(raw: string): ControlMessage | null {
  try {
    const msg = JSON.parse(raw) as ControlMessage;
    if (!msg || msg.v !== 1 || typeof msg.type !== "string") return null;
    return msg;
  } catch {
    return null;
  }
}

export class ViewerSession {
  private readonly opts: ViewerSessionOptions;
  private pc: RTCPeerConnection | null = null;
  private controlDc: RTCDataChannel | null = null;
  private tilesDc: RTCDataChannel | null = null;
  private fileDc: RTCDataChannel | null = null;
  private compositor: TileCompositor;
  private unsubSignaling: (() => void) | null = null;
  private statsTimer: number | null = null;
  private closed = false;
  private remoteDescriptionSet = false;
  private pendingRemoteIce: RTCIceCandidateInit[] = [];
  private turn: EphemeralTurnCredentials;
  private download: {
    transferId: string;
    name: string;
    size: number;
    chunks: Uint8Array[];
    pendingMeta: FileTransferChunkMeta | null;
  } | null = null;

  constructor(opts: ViewerSessionOptions) {
    this.opts = opts;
    this.turn = opts.turn;
    this.compositor = new TileCompositor(opts.canvas);
  }

  async start(): Promise<void> {
    this.setState("connecting");
    this.pc = new RTCPeerConnection({
      iceServers: iceServersFromTurn(this.turn),
      bundlePolicy: "max-bundle",
    });

    this.pc.onicecandidate = (ev) => {
      if (!ev.candidate || this.closed) return;
      void postSignal({
        sessionId: this.opts.sessionId,
        sessionJwt: this.opts.sessionJwt,
        kind: "ice",
        candidate: {
          candidate: ev.candidate.candidate,
          sdpMid: ev.candidate.sdpMid,
          sdpMLineIndex: ev.candidate.sdpMLineIndex,
          usernameFragment: ev.candidate.usernameFragment,
        },
      }).catch((err) => this.opts.onError?.(String(err)));
    };

    this.pc.onconnectionstatechange = () => {
      const s = this.pc?.connectionState;
      if (s === "connected") this.setState("connected");
      else if (s === "failed") this.setState("failed");
      else if (s === "closed" || s === "disconnected") {
        if (!this.closed) this.setState("closed");
      }
    };

    this.pc.ontrack = (ev) => {
      const stream = ev.streams[0] ?? new MediaStream([ev.track]);
      this.opts.video.srcObject = stream;
      void this.opts.video.play().catch(() => undefined);
      this.opts.onRemoteTrack?.(true);
    };

    this.controlDc = this.pc.createDataChannel(DATA_CHANNEL.control, { ordered: true });
    this.tilesDc = this.pc.createDataChannel(DATA_CHANNEL.tiles, { ordered: true });
    this.fileDc = this.pc.createDataChannel(DATA_CHANNEL.file, { ordered: true });

    this.wireControl(this.controlDc);
    this.wireTiles(this.tilesDc);
    this.wireFile(this.fileDc);

    this.unsubSignaling = subscribeSessionSignaling(
      this.opts.sessionId,
      (doc) => void this.onRemoteSignal(doc),
      (err) => this.opts.onError?.(err.message),
    );

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    await postSignal({
      sessionId: this.opts.sessionId,
      sessionJwt: this.opts.sessionJwt,
      kind: "offer",
      sdp: offer.sdp ?? "",
    });

    this.statsTimer = window.setInterval(() => {
      this.opts.onStats?.(this.compositor.getStats());
      this.opts.onCursor?.(this.compositor.getCursor());
    }, 500);

    this.controlDc.addEventListener("open", () => {
      this.sendSessionAuth();
      this.sendQuality(this.opts.preferredMode);
      this.sendModeSwitch(this.opts.encodingPreference);
    });
  }

  async refreshTurn(): Promise<void> {
    const callable = httpsCallable<MintTurnCredentialsRequest, MintTurnCredentialsResponse>(
      functions,
      CALLABLE_FUNCTIONS.mintTurnCredentials,
    );
    const result = await callable({
      sessionId: this.opts.sessionId,
      sessionJwt: this.opts.sessionJwt,
    });
    this.turn = result.data.turn;
    if (!this.pc) return;
    const config = this.pc.getConfiguration();
    config.iceServers = iceServersFromTurn(this.turn);
    this.pc.setConfiguration(config);
    const offer = await this.pc.createOffer({ iceRestart: true });
    await this.pc.setLocalDescription(offer);
    await postSignal({
      sessionId: this.opts.sessionId,
      sessionJwt: this.opts.sessionJwt,
      kind: "offer",
      sdp: offer.sdp ?? "",
    });
  }

  sendQuality(mode: QualityMode, hints?: { maxFps?: number; maxWidth?: number }): void {
    const msg: QualityUpdateMessage = {
      v: 1,
      type: "quality_update",
      mode,
      maxFps: hints?.maxFps,
      maxWidth: hints?.maxWidth,
    };
    this.sendJson(this.controlDc, msg);
  }

  sendModeSwitch(preference: EncodingPreference): void {
    const msg: ModeSwitchMessage = {
      v: 1,
      type: "mode_switch",
      preference,
    };
    this.sendJson(this.controlDc, msg);
  }

  sendMouse(msg: Omit<InputMouseMessage, "v" | "type">): void {
    const full: InputMouseMessage = { v: 1, type: "mouse", ...msg };
    this.sendJson(this.controlDc, full);
  }

  sendKey(msg: Omit<InputKeyboardMessage, "v" | "type">): void {
    const full: InputKeyboardMessage = { v: 1, type: "keyboard", ...msg };
    this.sendJson(this.controlDc, full);
  }

  sendWheel(msg: Omit<InputWheelMessage, "v" | "type">): void {
    const full: InputWheelMessage = { v: 1, type: "wheel", ...msg };
    this.sendJson(this.controlDc, full);
  }

  async uploadFile(file: File): Promise<string> {
    if (file.size > FILE_TRANSFER_LIMITS.maxFileBytes) {
      throw new Error(`File exceeds ${FILE_TRANSFER_LIMITS.maxFileBytes} byte limit.`);
    }
    const transferId = crypto.randomUUID();
    const hello: FileTransferHello = {
      v: 1,
      type: "file_hello",
      transferId,
      fileName: file.name,
      sizeBytes: file.size,
    };
    this.sendJson(this.fileDc, hello);

    const buf = await file.arrayBuffer();
    const chunkSize = FILE_TRANSFER_LIMITS.maxChunkBytes;
    let offset = 0;
    while (offset < buf.byteLength) {
      const length = Math.min(chunkSize, buf.byteLength - offset);
      const meta: FileTransferChunkMeta = {
        v: 1,
        type: "file_chunk",
        transferId,
        offset,
        length,
      };
      this.sendJson(this.fileDc, meta);
      const slice = new Uint8Array(buf, offset, length);
      this.fileDc?.send(slice);
      offset += length;
      this.opts.onFileEvent?.({
        type: "chunk",
        transferId,
        name: file.name,
        size: file.size,
        progress: offset / file.size,
      });
    }

    const done: FileTransferDone = { v: 1, type: "file_done", transferId, ok: true };
    this.sendJson(this.fileDc, done);
    this.opts.onFileEvent?.({ type: "done", transferId, name: file.name, size: file.size });
    return transferId;
  }

  mapClientToFramebuffer(clientX: number, clientY: number, el: HTMLElement): { x: number; y: number } {
    const rect = el.getBoundingClientRect();
    const fb = this.compositor.getFramebufferSize();
    const width = fb.width || el.clientWidth || 1;
    const height = fb.height || el.clientHeight || 1;
    const x = ((clientX - rect.left) / rect.width) * width;
    const y = ((clientY - rect.top) / rect.height) * height;
    return { x: Math.round(x), y: Math.round(y) };
  }

  async close(_reason = "viewer_hangup"): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.setState("closed");
    if (this.statsTimer != null) window.clearInterval(this.statsTimer);
    this.unsubSignaling?.();
    try {
      await postSignal({
        sessionId: this.opts.sessionId,
        sessionJwt: this.opts.sessionJwt,
        kind: "bye",
      });
    } catch {
      // ignore
    }
    this.controlDc?.close();
    this.tilesDc?.close();
    this.fileDc?.close();
    this.pc?.close();
    this.opts.video.srcObject = null;
  }

  private sendSessionAuth(): void {
    const msg: SessionAuthMessage = {
      v: 1,
      type: "session_auth",
      sessionId: this.opts.sessionId,
      sessionJwt: this.opts.sessionJwt,
    };
    this.sendJson(this.controlDc, msg);
  }

  private sendJson(dc: RTCDataChannel | null, msg: unknown): void {
    if (!dc || dc.readyState !== "open") return;
    dc.send(JSON.stringify(msg));
  }

  private wireControl(dc: RTCDataChannel): void {
    dc.binaryType = "arraybuffer";
    dc.onmessage = (ev) => {
      if (typeof ev.data !== "string") return;
      const msg = parseControl(ev.data);
      if (!msg) return;
      switch (msg.type) {
        case "session_auth_result":
          if (!msg.ok) this.opts.onError?.(msg.reason ?? "session_auth failed");
          if (msg.encodeMode) this.opts.onEncodeMode?.(msg.encodeMode);
          break;
        case "quality_update":
          if (msg.encodeMode) this.opts.onEncodeMode?.(msg.encodeMode);
          break;
        case "stats": {
          const stats = msg as StatsReportMessage;
          this.compositor.setStatsFromControl({
            fps: stats.fps,
            encodeMode: stats.encodeMode,
            throughputKbps: stats.throughputKbps,
          });
          this.opts.onEncodeMode?.(stats.encodeMode);
          break;
        }
        case "cursor":
          this.compositor.setCursorFromControl(msg);
          break;
        case "pong":
          break;
        default:
          break;
      }
    };
  }

  private wireTiles(dc: RTCDataChannel): void {
    dc.binaryType = "arraybuffer";
    dc.onmessage = (ev) => {
      if (!(ev.data instanceof ArrayBuffer)) return;
      this.compositor.handleBinary(ev.data);
    };
  }

  private wireFile(dc: RTCDataChannel): void {
    dc.binaryType = "arraybuffer";
    dc.onmessage = (ev) => {
      if (typeof ev.data === "string") {
        try {
          const msg = JSON.parse(ev.data) as FileTransferHello | FileTransferChunkMeta | FileTransferDone;
          if (msg.type === "file_hello") {
            this.download = {
              transferId: msg.transferId,
              name: msg.fileName,
              size: msg.sizeBytes,
              chunks: [],
              pendingMeta: null,
            };
            this.opts.onFileEvent?.({
              type: "hello",
              transferId: msg.transferId,
              name: msg.fileName,
              size: msg.sizeBytes,
            });
          } else if (msg.type === "file_chunk") {
            if (this.download?.transferId === msg.transferId) {
              this.download.pendingMeta = msg;
            }
          } else if (msg.type === "file_done") {
            if (msg.ok) this.finishDownload(msg.transferId);
            else {
              this.opts.onFileEvent?.({
                type: "error",
                transferId: msg.transferId,
                reason: msg.reason,
              });
              this.download = null;
            }
          }
        } catch {
          // ignore
        }
        return;
      }
      if (!(ev.data instanceof ArrayBuffer) || !this.download?.pendingMeta) return;
      const meta = this.download.pendingMeta;
      this.download.pendingMeta = null;
      this.download.chunks.push(new Uint8Array(ev.data));
      const received = this.download.chunks.reduce((n, c) => n + c.byteLength, 0);
      this.opts.onFileEvent?.({
        type: "chunk",
        transferId: meta.transferId,
        name: this.download.name,
        size: this.download.size,
        progress: this.download.size ? received / this.download.size : 0,
      });
    };
  }

  private finishDownload(transferId: string): void {
    const entry = this.download;
    if (!entry || entry.transferId !== transferId) return;
    const total = entry.chunks.reduce((n, c) => n + c.byteLength, 0);
    const out = new Uint8Array(total);
    let o = 0;
    for (const c of entry.chunks) {
      out.set(c, o);
      o += c.byteLength;
    }
    const blob = new Blob([out]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = entry.name || transferId;
    a.click();
    URL.revokeObjectURL(url);
    this.download = null;
    this.opts.onFileEvent?.({ type: "done", transferId, name: entry.name, size: entry.size });
  }

  private async onRemoteSignal(doc: SessionSignalDocument): Promise<void> {
    if (!this.pc || this.closed) return;
    try {
      if (doc.type === "answer" && doc.payload) {
        await this.pc.setRemoteDescription({ type: "answer", sdp: doc.payload });
        this.remoteDescriptionSet = true;
        for (const c of this.pendingRemoteIce) {
          await this.pc.addIceCandidate(c);
        }
        this.pendingRemoteIce = [];
      } else if (doc.type === "ice" && doc.payload) {
        const init = JSON.parse(doc.payload) as RTCIceCandidateInit;
        if (!this.remoteDescriptionSet) this.pendingRemoteIce.push(init);
        else await this.pc.addIceCandidate(init);
      } else if (doc.type === "hangup") {
        await this.close("remote_hangup");
      }
    } catch (err) {
      this.opts.onError?.(String(err));
    }
  }

  private setState(state: ViewerConnectionState): void {
    this.opts.onState?.(state);
  }
}
